/**
 * Section « WhatsApp Business » du SuperAdmin — page d'information & configuration
 * (parité legacy sa-whatsapp) : pourquoi WhatsApp, étapes de setup Meta Cloud API,
 * templates de notification, recommandation tarifaire. Contenu guide (statique).
 */
const STEPS: { title: string; desc: string }[] = [
  { title: 'Créer un compte Meta Business Manager', desc: 'business.facebook.com → Créer une entreprise (Lil & Co Services). Lier ton compte Facebook perso.' },
  { title: 'Ajouter WhatsApp Business à ton portefeuille', desc: 'Dans Meta Business → Paramètres → Comptes WhatsApp Business → Créer. Tu obtiens un numéro WhatsApp dédié Lil & Co (peut être un numéro virtuel).' },
  { title: 'Vérifier ton entreprise', desc: 'Meta demande KBIS + justificatif domicile. Vérification 1-3 jours ouvrés. Niveau de tiers : démarre à 1000 messages/jour, monte à 100K avec usage.' },
  { title: 'Créer 4 templates de notification', desc: 'Modèles à soumettre à Meta pour validation (24-48h) : (1) Confirmation commande, (2) Commande acceptée, (3) Commande prête, (4) Récupération + lien Google Review. Variables : prénom, n° cmd, restaurant, heure retrait.' },
  { title: 'Générer access token + Phone Number ID', desc: "Dans WhatsApp API Setup, récupère ton token permanent et l'ID du numéro. À copier dans les secrets Supabase (WHATSAPP_TOKEN, WHATSAPP_PHONE_NUMBER_ID)." },
  { title: 'Connecter l’Edge Function send-whatsapp', desc: 'La fonction Supabase `send-whatsapp` envoie les notifications via Meta Cloud API. Déjà présente dans le projet — renseigner les secrets suffit.' },
  { title: 'Activer par restaurant', desc: 'Toggle indépendant par resto (option premium). Recommandé : +15 €/mois HT par resto.' },
];

const TEMPLATES: { title: string; code: string; body: string }[] = [
  { title: 'Confirmation commande', code: 'order_confirmation', body: 'Bonjour {{1}} 👋\nVotre commande {{2}} chez {{3}} est confirmée ✅\nMontant : {{4}}\nRetrait prévu à {{5}}\n\nMerci de votre confiance !' },
  { title: 'Commande acceptée par le resto', code: 'order_accepted', body: '{{1}}, votre commande {{2}} chez {{3}} est en cuisine 🍳\nElle sera prête à {{4}}.\n\nÀ tout de suite !' },
  { title: 'Commande prête à récupérer', code: 'order_ready', body: '🎉 {{1}}, votre commande {{2}} est prête !\n\n📍 {{3}}\n📞 {{4}}\n\nBon appétit 😋' },
  { title: "Demande d'avis Google (24h après)", code: 'order_review_request', body: "Merci {{1}} pour votre visite chez {{2}} 🙏\nSi vous avez aimé, ça nous aiderait beaucoup d'avoir votre avis ⭐\n\n👉 {{3}}" },
];

export default function WhatsAppSetup() {
  return (
    <div className="max-w-4xl">
      <div className="mb-4 rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
        <div className="text-xs font-bold uppercase opacity-80">💬 WhatsApp Business — point fort différenciant</div>
        <div className="text-2xl font-extrabold">Notifications client par WhatsApp — taux de lecture 98%</div>
        <div className="text-sm opacity-90">Confirmation · acceptation · prête · retrait. Bien plus efficace que SMS ou email.</div>
      </div>

      <div className="mb-5 grid gap-3 lg:grid-cols-3">
        <InfoCard icon="📱" title="98% de taux de lecture" desc="Vs ~25% pour les emails et 80% pour les SMS. WhatsApp est l'app n°1 utilisée en France." />
        <InfoCard icon="💸" title="Gratuit jusqu'à 1000 conv/mois" desc="Meta WhatsApp Cloud API : 0 € pour les 1000 premiers messages. Au-delà : ~0,024 €/notification (FR)." />
        <InfoCard icon="🚀" title="Argument de vente" desc="« Nos clients sont notifiés sur WhatsApp en temps réel » — argument fort vs concurrence Click&Collect." />
      </div>

      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-base font-bold">⚙️ Étapes de configuration WhatsApp Business Cloud API</h2>
          <span className="rounded-md bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">À ACTIVER</span>
        </div>
        <p className="mb-3 text-sm text-slate-500">À faire 1 seule fois, pour tout le réseau Lil &amp; Co.</p>
        <ol className="flex flex-col gap-3">
          {STEPS.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: '#25D366' }}>{i + 1}</span>
              <div>
                <div className="font-semibold">{s.title}</div>
                <div className="text-sm text-slate-500">{s.desc}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-base font-bold">📝 Templates WhatsApp proposés (à soumettre à Meta pour validation)</h2>
        <div className="flex flex-col gap-3">
          {TEMPLATES.map((t) => (
            <div key={t.code} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-semibold">{t.title}</span>
                <code className="rounded bg-slate-200 px-2 py-0.5 text-xs">{t.code}</code>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700">{t.body}</pre>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-400">Variables : <code>{'{{1}}'}</code> prénom · <code>{'{{2}}'}</code> n° commande · <code>{'{{3}}'}</code> resto · etc.</p>
      </div>

      <div className="rounded-xl bg-sky-50 p-4 text-sm text-sky-900">
        💡 <strong>Recommandation tarification</strong> : facture le module WhatsApp en option premium <strong>+15 €/mois HT</strong> par resto
        (marge nette ~10 €/mois après coûts Meta). Argumentaire : « 98% de vos clients liront leur notification, vs 25% par email ».
      </div>
    </div>
  );
}

function InfoCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-2xl">{icon}</div>
      <div className="mt-1 font-bold">{title}</div>
      <div className="mt-1 text-sm text-slate-500">{desc}</div>
    </div>
  );
}
